import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { 
  Package, FolderTree, Building2, MousePointerClick,
  TrendingUp, ArrowUpRight, Clock, Wifi, WifiOff, RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from "recharts";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [recentClicks, setRecentClicks] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Fetch initial data
  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/analytics/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
      setRecentClicks(response.data.recent_clicks || []);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const wsUrl = `${WS_URL}/ws/dashboard`;
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        setWsConnected(true);
        toast.success("Gerçek zamanlı bağlantı kuruldu");
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === "new_click") {
            // Add new click to the beginning of the list
            setRecentClicks(prev => [data.click, ...prev.slice(0, 19)]);
            
            // Update total clicks
            setStats(prev => prev ? {
              ...prev,
              total_clicks: data.total_clicks
            } : prev);

            // Show toast notification
            toast.info(`Yeni Tıklama: ${data.click.tariff_name}`, {
              description: `${data.click.provider_name} - ${data.click.category_name}`
            });

            // Refresh full stats every 5 clicks for chart updates
            if (data.total_clicks % 5 === 0) {
              fetchStats();
            }
          }
          
          if (data.type === "seo_analysis") {
            toast.success(`SEO Analizi Tamamlandı`, {
              description: `${data.analysis.page_type} - Skor: ${data.analysis.score}`
            });
          }
        } catch (e) {
          console.error("WS message parse error:", e);
        }
      };

      wsRef.current.onclose = () => {
        setWsConnected(false);
        // Reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, 3000);
      };

      wsRef.current.onerror = (error) => {
        console.error("WebSocket error:", error);
        setWsConnected(false);
      };
    } catch (error) {
      console.error("WebSocket connection error:", error);
      setWsConnected(false);
    }
  }, [fetchStats]);

  useEffect(() => {
    fetchStats();
    connectWebSocket();

    // Cleanup on unmount
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [fetchStats, connectWebSocket]);

  // Auto refresh every 30 seconds as fallback
  useEffect(() => {
    const interval = setInterval(() => {
      if (!wsConnected) {
        fetchStats();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [wsConnected, fetchStats]);

  const statCards = [
    {
      title: "Toplam Tarife",
      value: stats?.total_tariffs || 0,
      icon: Package,
      color: "bg-electric-500",
    },
    {
      title: "Kategoriler",
      value: stats?.total_categories || 0,
      icon: FolderTree,
      color: "bg-signal-500",
    },
    {
      title: "Sağlayıcılar",
      value: stats?.total_providers || 0,
      icon: Building2,
      color: "bg-purple-500",
    },
    {
      title: "Toplam Tıklama",
      value: stats?.total_clicks || 0,
      icon: MousePointerClick,
      color: "bg-orange-500",
    },
  ];

  // Prepare chart data
  const categoryChartData = stats?.category_clicks?.map(cat => ({
    name: cat.name || cat._id,
    clicks: cat.clicks || 0,
    tariffs: cat.tariff_count || 0
  })) || [];

  const topTariffsData = stats?.top_tariffs?.slice(0, 5).map(t => ({
    name: t.name?.substring(0, 15) + (t.name?.length > 15 ? '...' : ''),
    clicks: t.click_count || 0,
    provider: t.provider_name
  })) || [];

  const dailyClicksData = stats?.daily_clicks?.map(d => ({
    day: d._id?.substring(5) || '',
    clicks: d.clicks || 0
  })) || [];

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Connection Status Bar */}
        <div className="flex items-center justify-between bg-white rounded-lg px-4 py-2 shadow-sm">
          <div className="flex items-center gap-2">
            {wsConnected ? (
              <>
                <Wifi className="w-4 h-4 text-signal-500" />
                <span className="text-sm text-signal-600 font-medium">Canlı Bağlantı Aktif</span>
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-signal-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-signal-500"></span>
                </span>
              </>
            ) : (
              <>
                <WifiOff className="w-4 h-4 text-red-500" />
                <span className="text-sm text-red-600 font-medium">Bağlantı Kesildi</span>
              </>
            )}
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              fetchStats();
              toast.info("Veriler yenileniyor...");
            }}
            className="text-slate-600"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Yenile
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, index) => (
            <Card key={index} className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-navy-900">
                      {loading ? "..." : stat.value.toLocaleString()}
                    </p>
                  </div>
                  <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Category Clicks Chart */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Kategori Bazlı Tıklamalar</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-64 bg-slate-100 rounded animate-pulse"></div>
              ) : categoryChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={categoryChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="clicks" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Tıklama" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-500">
                  Henüz tıklama verisi yok
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Tariffs Chart */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg">En Çok Tıklanan Tarifeler</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-64 bg-slate-100 rounded animate-pulse"></div>
              ) : topTariffsData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={topTariffsData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [value, 'Tıklama']}
                    />
                    <Bar dataKey="clicks" fill="#10B981" radius={[0, 4, 4, 0]} name="Tıklama" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-500">
                  Henüz tıklama verisi yok
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Daily Clicks & Category Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Daily Clicks Line Chart */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Son 7 Gün Tıklama Trendi</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-64 bg-slate-100 rounded animate-pulse"></div>
              ) : dailyClicksData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={dailyClicksData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="clicks" 
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      dot={{ fill: '#3B82F6', strokeWidth: 2 }}
                      name="Tıklama"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-500">
                  Henüz tıklama verisi yok
                </div>
              )}
            </CardContent>
          </Card>

          {/* Category Distribution Pie Chart */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Kategori Dağılımı</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-64 bg-slate-100 rounded animate-pulse"></div>
              ) : categoryChartData.some(c => c.clicks > 0) ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={categoryChartData.filter(c => c.clicks > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      fill="#8884d8"
                      paddingAngle={5}
                      dataKey="clicks"
                      nameKey="name"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {categoryChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-500">
                  Henüz tıklama verisi yok
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Clicks - Real-time */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-electric-500" />
                Son Tıklanılan Hizmetler
                <Badge variant="outline" className="ml-2 text-xs">
                  Canlı
                </Badge>
              </CardTitle>
              {wsConnected && (
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-signal-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-signal-500"></span>
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-14 bg-slate-100 rounded animate-pulse"></div>
                ))}
              </div>
            ) : recentClicks.length > 0 ? (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {recentClicks.map((click, index) => (
                  <div 
                    key={click.id || index} 
                    className={`flex items-center justify-between p-3 rounded-lg transition-all duration-300 ${
                      index === 0 ? 'bg-signal-50 border border-signal-200' : 'bg-slate-50 hover:bg-slate-100'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        index === 0 ? 'bg-signal-100' : 'bg-electric-100'
                      }`}>
                        <MousePointerClick className={`w-5 h-5 ${
                          index === 0 ? 'text-signal-600' : 'text-electric-600'
                        }`} />
                      </div>
                      <div>
                        <p className="font-medium text-navy-900">
                          {click.tariff_name || 'Bilinmeyen Tarife'}
                          {index === 0 && (
                            <Badge className="ml-2 bg-signal-500 text-white text-xs">Yeni</Badge>
                          )}
                        </p>
                        <p className="text-sm text-slate-500">
                          {click.provider_name && `${click.provider_name} • `}
                          {click.category_name || 'Kategori'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">
                        {click.created_at ? new Date(click.created_at).toLocaleString('tr-TR', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        }) : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                Henüz tıklama kaydı yok. Kullanıcılar "Zum Anbieter" butonuna tıkladığında burada anlık olarak görünecek.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Hızlı İşlemler</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <a 
              href="/admin/tarifeler" 
              className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-electric-500" />
                <span className="font-medium">Yeni Tarife Ekle</span>
              </div>
              <ArrowUpRight className="w-4 h-4 text-slate-400" />
            </a>
            <a 
              href="/admin/seo" 
              className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-signal-500" />
                <span className="font-medium">AI SEO Analizi</span>
              </div>
              <ArrowUpRight className="w-4 h-4 text-slate-400" />
            </a>
            <a 
              href="/admin/google-entegrasyonlari" 
              className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-purple-500" />
                <span className="font-medium">Google Ayarları</span>
              </div>
              <ArrowUpRight className="w-4 h-4 text-slate-400" />
            </a>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
